import React, { useRef } from 'react';

import { faSort, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { flexRender } from '@tanstack/react-table';
import type {
  Cell,
  Header,
  HeaderGroup,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import { cn } from '../lib/utils';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table';

export const DataTable = <TData, TValue>({
  table,
  isLoading,
  className,
}: {
  table: ReturnType<typeof useReactTable<TData>>;
  isLoading?: boolean;
  className?: string;
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { rows } = table.getRowModel();

  const [hoveredRowId, setHoveredRowId] = React.useState<string | null>(null);

  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    estimateSize: () => 5,
    getScrollElement: () => tableContainerRef.current,
  });

  return (
    <div className={cn('vai-flex vai-flex-col vai-gap-4', className)}>
      <div
        className="vai-relative vai-overflow-auto vai-border vai-border-gray-200 vai-rounded-md"
        ref={tableContainerRef}
      >
        <Table>
          <TableHeader className="vai-sticky vai-top-0 vai-z-10 vai-bg-zinc-50">
            {table.getHeaderGroups().map((headerGroup: HeaderGroup<TData>) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header: Header<TData, unknown>) => (
                  <TableHead
                    key={header.id}
                    className={
                      header.column.getCanSort()
                        ? 'vai-cursor-pointer hover:vai-bg-gray-50'
                        : ''
                    }
                    style={{
                      width: `${100 / headerGroup.headers.length}%`,
                      minWidth: `${header.column.getSize()}px`,
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="vai-flex vai-items-center vai-gap-1">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {header.column.getCanSort() && (
                        <span className="vai-text-gray-400 vai-p-1">
                          {{
                            asc: (
                              <span>
                                <FontAwesomeIcon icon={faSort} />
                              </span>
                            ),
                            desc: (
                              <span>
                                <FontAwesomeIcon icon={faSort} />
                              </span>
                            ),
                          }[header.column.getIsSorted() as string] ?? (
                            <FontAwesomeIcon icon={faSort} />
                          )}
                        </span>
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          {isLoading && (
            <TableBody className="vai-bg-white">
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleFlatColumns().length}
                  className="vai-h-24 vai-text-center"
                >
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="vai-h-7 vai-w-7 vai-text-zinc-200 vai-animate-spin"
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          )}
          {rows.length > 0 && !isLoading && (
            <TableBody
              className="vai-bg-white vai-flex"
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index] as Row<TData>;

                return (
                  <TableRow
                    data-index={virtualRow.index}
                    ref={(node) => rowVirtualizer.measureElement(node)}
                    key={row.id}
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    className={cn(
                      'vai-bg-white vai-min-w-full vai-absolute vai-flex group',
                      row.getIsSelected() && 'vai-bg-gray-50',
                    )}
                    onClick={() => row.toggleSelected()}
                    onMouseEnter={() => setHoveredRowId(row.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                  >
                    {row.getVisibleCells().map((cell: Cell<TData, TValue>) => {
                      return (
                        <TableCell
                          key={cell.id}
                          style={{
                            width: `${100 / row.getVisibleCells().length}%`,
                            minWidth: `${cell.column.getSize()}px`,
                          }}
                          className="vai-flex vai-items-center"
                        >
                          {flexRender(cell.column.columnDef.cell, {
                            ...cell.getContext(),
                            hoveredRowId,
                          })}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          )}
          {!isLoading && !rows.length && (
            <TableBody className="vai-bg-white">
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleFlatColumns().length}
                  className="vai-h-24 vai-text-center"
                >
                  No results found.
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </div>
    </div>
  );
};
